import { useEffect, useMemo, useRef, useState } from 'react'
import { aggregateSimple, getQuestionValue, getQuestionValues } from '../chart-aggregation'
import {
  asFiniteNumber,
  binCounts,
  continuousBinEdges,
  continuousBinLabels,
  isLowReaderBase,
  readContinuousDomain,
} from '../../../utils/continuousBinning'
import { CHART_LIMITS } from '../../../foundations/slide-limits'
import { getScaleAxisLayout } from './scale-axis-layout'
import { DivergingBar } from '../DivergingBar'
import {
  bandCaptionTextClass,
  fiveBandFromDistribution,
  formatFiveBandLabel,
} from '../diverging'
import { readPolarity } from '../MatrixChart/matrixThreeBand'
import {
  formatAssetReportLabel,
  type AssetReportContextItem,
} from '../asset-report-label'
import { LaneMergedSections } from '../LaneMergedSections'
import {
  unfoldResponsesForLane,
  declaresPerAssetBinding,
  type MonadicLaneScopeFact,
} from '../../../lib/monadicLaneScope'

const BAR_COLOR = 'var(--slide-chart-1)'

/**
 * Chrome for the continuous histogram. deck-ui has no i18n runtime; the report
 * passes translated strings and every other caller keeps these English defaults.
 * `basedOn` and `showingTop` are templates with `{{count}}`, `{{shown}}`, `{{total}}`.
 */
export interface NumericChartLabels {
  median: string
  average: string
  spread: string
  rangePrefix: string
  rangeSeparator: string
  basedOn: string
  lowBase: string
  noData: string
  showingTop: string
  priceRoleTooCheap: string
  priceRoleCheap: string
  priceRoleExpensive: string
  priceRoleTooExpensive: string
}

export const DEFAULT_NUMERIC_CHART_LABELS: NumericChartLabels = {
  median: 'Median',
  average: 'Average',
  spread: 'P25 to P75',
  rangePrefix: 'Range',
  rangeSeparator: 'to',
  basedOn: 'Based on {{count}} responses',
  lowBase: 'Low base, read as directional',
  noData: 'No data available',
  showingTop: 'Showing top {{shown}} of {{total}} segments by respondent count.',
  priceRoleTooCheap: 'Price sensitivity: too cheap',
  priceRoleCheap: 'Price sensitivity: cheap',
  priceRoleExpensive: 'Price sensitivity: expensive',
  priceRoleTooExpensive: 'Price sensitivity: too expensive',
}

const PRICE_ROLES = ['too_cheap', 'cheap', 'expensive', 'too_expensive'] as const

interface NumericChartProps {
  question: any
  segmentQuestion?: any
  responses: any[]
  colors?: string[]
  assetContextItems?: Array<AssetReportContextItem>
  // Currency/unit symbol ("$", "€", …) prefixed onto continuous figures when the
  // question has no ISO currency code. `currencyCode` wins when both are set.
  valuePrefix?: string
  // ISO code stamped by the compiler on `_canonical.currencyCode`. Formats through
  // Intl so the symbol, grouping and placement follow `locale`.
  currencyCode?: string | null
  // UI locale (`en-US`, `pt-BR`). Omitted callers keep en-US grouping, which is
  // what the prefix path printed before this card grew a histogram.
  locale?: string
  labels?: Partial<NumericChartLabels>
  // This question's declared Van Westendorp role, stamped on the compiled
  // question. The map below remains for callers that still pass the study-level
  // `questionIdByRole` binding. Neither is read from the question's text.
  priceRole?: string | null
  priceCurveRoles?: Record<string, string>
  monadicLaneScope?: MonadicLaneScopeFact | null
  emptyLaneLabel?: string
  // Mirror of the questionMap entry's `assetBinding`, the same fallback
  // `resolveCardLaneScope` reads (QuestionCard `canonicalQuestionMap[name].assetBinding`)
  // when the compiled element carries no `_canonical.assetBinding` of its own.
  entryBinding?: { mode?: unknown } | null
}

/**
 * Chip copy per declared price role. Keyed on the closed contract enum
 * (`PriceRole` in shared-types), never on anything a survey author writes.
 */
function isPriceRole(role: string | null | undefined): role is (typeof PRICE_ROLES)[number] {
  return typeof role === 'string' && (PRICE_ROLES as ReadonlyArray<string>).includes(role)
}

function priceRoleChip(labels: NumericChartLabels, role: string): string | null {
  if (role === 'too_cheap') return labels.priceRoleTooCheap
  if (role === 'cheap') return labels.priceRoleCheap
  if (role === 'expensive') return labels.priceRoleExpensive
  if (role === 'too_expensive') return labels.priceRoleTooExpensive
  return null
}

function fillLabel(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => String(values[key] ?? ''))
}

/**
 * One formatter for every continuous figure: stats, axis ticks, segment means.
 *
 * An ISO currency goes through Intl, which places the symbol and the grouping.
 * A bare number, or a `valuePrefix` symbol, uses the same digit rule the card
 * already had: integers as whole numbers, one decimal from 10 up, two below.
 */
function formatFigure(
  value: number,
  options: {
    currencyCode?: string | null
    locale?: string
    valuePrefix?: string
    decimals?: number
  },
): string {
  const currency = (options.currencyCode || '').trim()
  const prefix = (options.valuePrefix || '').trim()
  const locale = options.locale || 'en-US'
  if (currency) {
    const digits = options.decimals
    return value.toLocaleString(locale, {
      style: 'currency',
      currency,
      ...(digits !== undefined
        ? { minimumFractionDigits: digits, maximumFractionDigits: digits }
        : {}),
    })
  }
  const digits = options.decimals !== undefined
    ? options.decimals
    : Number.isInteger(value)
      ? 0
      : Math.abs(value) >= 100
        ? 0
        : Math.abs(value) >= 10
          ? 1
          : 2
  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  return prefix ? `${prefix}${formatted}` : formatted
}

/** Hyndman-Fan type 7. p50 matches the median this card already printed. */
function quantile(sorted: Array<number>, p: number): number {
  if (sorted.length === 1) return sorted[0]
  const index = (sorted.length - 1) * p
  const lower = Math.floor(index)
  const upper = Math.ceil(index)
  if (lower === upper) return sorted[lower]
  const weight = index - lower
  return sorted[lower] * (1 - weight) + sorted[upper] * weight
}

/** The declared role this question fills in the price curve, or null when it fills none. */
function readDeclaredPriceRole(
  question: any,
  priceCurveRoles: Record<string, string> | undefined,
  priceRole: string | null | undefined,
): string | null {
  if (isPriceRole(priceRole)) {
    return priceRole
  }
  if (!priceCurveRoles) {
    return null
  }
  const canonicalId = question?._canonical?.id
  const names = [canonicalId, question?.name].filter((value) => typeof value === 'string' && value)
  for (const [role, questionId] of Object.entries(priceCurveRoles)) {
    if (names.includes(questionId) && isPriceRole(role)) {
      return role
    }
  }
  return null
}

/**
 * Helper to get raw numeric values from responses for a question
 */
function getRawNumericValues(question: any, responses: any[]): number[] {
  const values: number[] = []
  
  responses.forEach(r => {
    const value = getQuestionValue(r, question)
    const num = asFiniteNumber(value)
    if (num !== null) {
      values.push(num)
    }
  })
  
  return values.sort((a, b) => a - b)
}

/**
 * Calculate comprehensive statistics for numeric data
 */
function calculateStats(values: number[]) {
  if (values.length === 0) return null
  
  const sorted = [...values].sort((a, b) => a - b)
  const n = sorted.length
  
  const sum = sorted.reduce((a, b) => a + b, 0)
  const mean = sum / n
  
  const median = quantile(sorted, 0.5)
  const p25 = quantile(sorted, 0.25)
  const p75 = quantile(sorted, 0.75)
  const min = sorted[0]
  const max = sorted[n - 1]

  return { mean, median, min, max, p25, p75, count: n }
}

interface SegmentHistogramData {
  segmentName: string
  histogram: { bin: string; count: number; start: number; end: number }[]
  mean: number
  count: number
  totalSegmentCount: number
  /** The last bucket is open-ended: its ceiling is one answer, not a fact about the group. */
  hasOverflow: boolean
}

function scaleToPercent(value: number, min: number, max: number, fallback = 0): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max)) return fallback
  const span = max - min
  if (!Number.isFinite(span) || span <= 0) return fallback
  const percent = ((value - min) / span) * 100
  if (!Number.isFinite(percent)) return fallback
  return Math.max(0, Math.min(100, percent))
}

/**
 * Aggregate continuous data with segmentation, producing mini histograms per segment
 * Uses consistent bin edges across all segments for fair visual comparison
 */
function aggregateContinuousSegmented(
  question: any,
  segmentQuestion: any,
  responses: any[]
): SegmentHistogramData[] {
  if (!responses || responses.length === 0) return []

  // First, group responses by segment
  const segmentGroups: Record<string, any[]> = {}
  
  responses.forEach(r => {
    const segmentValues = new Set(getQuestionValues(r, segmentQuestion).map(String))
    segmentValues.forEach((segmentStr) => {
      if (!segmentGroups[segmentStr]) {
        segmentGroups[segmentStr] = []
      }
      segmentGroups[segmentStr].push(r)
    })
  })

  // Collect ALL numeric values across all segments to determine one shared grid
  const allValues: number[] = []
  Object.values(segmentGroups).forEach(segmentResponses => {
    const values = getRawNumericValues(question, segmentResponses)
    allValues.push(...values)
  })

  if (allValues.length === 0) return []

  // D9: the edges are decided once, on every segment's answers together, and every
  // segment is then counted into them. Two segments of one question binned on
  // different boundaries compare different things side by side without saying so.
  // Superseded here: a local `getNiceStep` over 8 bins with no tail cut and no bin
  // ceiling, which was also a second, different copy of the deck's own rule.
  const binning = continuousBinEdges(allValues, readContinuousDomain(question))
  const binLabels = continuousBinLabels(binning)

  // Sort segments by count and limit to top segments
  const sortedSegmentNames = Object.entries(segmentGroups)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, CHART_LIMITS.numericMaxSegments)
    .map(([name]) => name)
  const totalSegmentCount = Object.keys(segmentGroups).length

  // For each segment, bin the values and calculate stats
  const result: SegmentHistogramData[] = sortedSegmentNames.map(segmentName => {
    const segmentResponses = segmentGroups[segmentName]
    const values = getRawNumericValues(question, segmentResponses)
    const counts = binCounts(values, binning.edges)

    // Calculate mean
    const mean = values.length > 0 
      ? values.reduce((a, b) => a + b, 0) / values.length 
      : 0

    return {
      segmentName,
      histogram: binLabels.map((label, idx) => ({
        bin: label,
        count: counts[idx],
        start: binning.edges[idx].low,
        end: binning.edges[idx].high,
      })),
      mean,
      count: values.length,
      totalSegmentCount,
      hasOverflow: binning.hasOverflow,
    }
  })

  return result
}

function formatAssetLabel(assetKey: string): string {
  return assetKey
    .replace(/^asset_/, '')
    .replace(/_(\d+)$/, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function ContinuousBars(props: {
  bins: Array<{ count: number; low: number; high: number; label: string }>
  maxCount: number
  total: number
  barColor: string
  heightClass: string
  showBars?: boolean
  formatTick: (value: number) => string
  testIdFor: (index: number) => string
  hasOverflow: boolean
  showAxis: boolean
  axisStartId?: string
  axisEndId?: string
}) {
  const last = props.bins.length - 1
  const showBars = props.showBars !== false
  return (
    <div>
      {showBars && (
      <div className={`flex items-end gap-0.5 ${props.heightClass}`}>
        {props.bins.map((bin, index) => {
          const share = props.total > 0 ? Math.round((bin.count / props.total) * 100) : 0
          const heightPercent = props.maxCount > 0 ? (bin.count / props.maxCount) * 100 : 0
          return (
            <div key={index} className="flex h-full min-w-0 flex-1 flex-col">
              <span className="mb-0.5 shrink-0 text-center text-[10px] leading-none tabular-nums text-muted-foreground">
                {bin.count}
              </span>
              <div className="flex min-h-0 flex-1 items-end">
              <div
                className="w-full rounded-t"
                style={{
                  height: bin.count > 0 ? `${Math.max(heightPercent, 4)}%` : '0%',
                  backgroundColor: props.barColor,
                }}
                title={`${share}%`}
                data-testid={props.testIdFor(index)}
                data-bin-label={bin.label}
                data-bin-count={bin.count}
                data-bin-start={bin.low}
                data-bin-end={bin.high}
              />
              </div>
            </div>
          )
        })}
      </div>
      )}
      {props.showAxis && props.bins.length > 0 && (
        <div className="relative mt-1 flex text-[10px] text-muted-foreground">
          {props.bins.map((bin, index) => (
            <span
              key={index}
              className="min-w-0 flex-1 truncate"
              data-testid={index === 0 ? props.axisStartId : undefined}
            >
              {props.formatTick(bin.low)}
            </span>
          ))}
          <span
            className="absolute right-0 max-w-[40%] truncate bg-inherit"
            data-testid={props.axisEndId}
          >
            {props.hasOverflow
              ? `${props.formatTick(props.bins[last].low)}+`
              : props.formatTick(props.bins[last].high)}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function NumericChart(props: NumericChartProps) {
  const { question, responses, monadicLaneScope, assetContextItems, emptyLaneLabel } = props
  if (unfoldResponsesForLane(question, responses, monadicLaneScope).length > 0) {
    return (
      <LaneMergedSections
        question={question}
        responses={responses}
        monadicLaneScope={monadicLaneScope}
        assetContextItems={assetContextItems}
        testIdPrefix="numeric-chart"
        emptyLabel={emptyLaneLabel}
      >
        {(unfolded) => (
          <NumericChartBody {...props} responses={unfolded} segmentQuestion={undefined} monadicLaneScope={null} />
        )}
      </LaneMergedSections>
    )
  }
  return <NumericChartBody {...props} />
}

function NumericChartBody({ question, segmentQuestion, responses, colors: colorsProp, assetContextItems, valuePrefix, currencyCode, locale, labels: labelsProp, priceRole, priceCurveRoles, monadicLaneScope, entryBinding }: NumericChartProps) {
  const effectiveBarColor = colorsProp?.[0] || BAR_COLOR
  const labels: NumericChartLabels = { ...DEFAULT_NUMERIC_CHART_LABELS, ...labelsProp }
  const isSegmented = !!segmentQuestion

  // Segment values are raw ids (e.g. `asset_ad_2`); segmentQuestion.choices
  // already carries the authored asset title when one exists (see
  // buildAssetSegmentQuestion), so prefer it over reformatting the id.
  const segmentLabels = useMemo(() => {
    const map = new Map<string, string>()
    for (const choice of segmentQuestion?.choices || []) {
      const value = String(choice?.value ?? '').trim()
      const text = typeof choice?.text === 'string' ? choice.text.trim() : ''
      if (value && text) map.set(value, text)
    }
    return map
  }, [segmentQuestion])
  const segmentLabel = (segmentName: string) => {
    const fromChoices = segmentLabels.get(segmentName)
    if (fromChoices) return fromChoices
    const fromAssets = formatAssetReportLabel(segmentName, assetContextItems)
    if (fromAssets !== segmentName) return fromAssets
    return formatAssetLabel(segmentName)
  }
  const formatAmount = (value: number) => formatFigure(value, { currencyCode, locale, valuePrefix })

  // QS-58 — measure the chart's OWN width (container-relative, not viewport) so the
  // Likert scale-axis row can switch to a non-overlapping layout in the narrow report
  // panel while staying byte-identical at the wide deck/export width. `undefined` until
  // measured → treated as wide, so first paint / SSR match the established layout.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined)
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const measure = () => setContainerWidth(el.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const scaleAxis = getScaleAxisLayout(containerWidth)
  
  // Detect if this is a Likert/rating scale or continuous numeric data
  const isLikertScale = question.type === 'rating' || 
    question.rateValues || 
    question.rateMin !== undefined ||
    question.rateMax !== undefined

  // Per-asset scope is the study-level lane fact (`monadicLaneScope`), which
  // `NumericChart` already unfolded above. The dict-shape probe below is the
  // HISTORICAL reader only: an analysis written before the fact existed stores
  // `{asset_lp_1: 4, asset_lp_2: 3}` with no typed scope to read. When the fact
  // is present and does not name this question, a map shape is not a scope,
  // unless the question is declared `per_asset`:
  // Python's `_metric_is_asset_keyed` reads that binding before lane membership,
  // and the browser follows it. Binding
  // alone must not win after a lane unfold either: those values are already
  // scalars, so the map shape is still required.
  const hasLaneFact = monadicLaneScope != null
  const isPerAsset: boolean = (!hasLaneFact || declaresPerAssetBinding(question, entryBinding)) && isLikertScale && responses.some((r: any) => {
    const v = getQuestionValue(r, question)
    return v !== null && v !== undefined && typeof v === 'object' && !Array.isArray(v) &&
      Object.values(v as Record<string, unknown>).some((val) => typeof val === 'number')
  })

  // Get raw numeric values for statistics (for continuous data)
  const rawValues = useMemo(() => {
    if (isLikertScale) return []
    return getRawNumericValues(question, responses)
  }, [question, responses, isLikertScale])

  // Calculate statistics for continuous data
  const stats = useMemo(() => {
    if (isLikertScale) return null
    return calculateStats(rawValues)
  }, [rawValues, isLikertScale])

  // Get binned data for histogram
  const histogramData = useMemo(() => {
    if (isLikertScale) return []
    const data = aggregateSimple(question, responses)
    return data.sort((a, b) => Number(a.rawValue) - Number(b.rawValue))
  }, [question, responses, isLikertScale])

  // The grid the continuous card is drawn on. Same utility, same edges as the
  // segmented sparklines and as the deck, so the tab and the slide cannot disagree
  // about where a bucket starts.
  const continuousBinning = useMemo(() => {
    if (isLikertScale) return null
    return continuousBinEdges(rawValues, readContinuousDomain(question))
  }, [rawValues, question, isLikertScale])

  // Which of the four price-curve thresholds this question is, from the declared
  // roles only. Absent binding, absent chip.
  const declaredPriceRole = readDeclaredPriceRole(question, priceCurveRoles, priceRole)
  const roleChip = declaredPriceRole ? priceRoleChip(labels, declaredPriceRole) : null

  // Likert scale config (for rating questions)
  const scaleRange = useMemo(() => {
    if (!isLikertScale) return { min: 1, max: 5, minLabel: '1', maxLabel: '5' }
    
    if (question.rateValues && question.rateValues.length > 0) {
      const values = question.rateValues.map((v: any) => {
        const val = typeof v === 'object' ? (v.value ?? v.text) : v
        return Number(val)
      }).filter((v: number) => !isNaN(v) && Number.isFinite(v))
      // Guard against empty values array after filtering
      if (values.length === 0) {
        return { min: 1, max: 5, minLabel: '1', maxLabel: '5' }
      }
      return {
        min: Math.min(...values),
        max: Math.max(...values),
        minLabel: question.minRateDescription || String(Math.min(...values)),
        maxLabel: question.maxRateDescription || String(Math.max(...values))
      }
    }
    const min = question.rateMin != null ? question.rateMin : 1
    const max = question.rateMax != null ? question.rateMax : 5
    return {
      min,
      max,
      minLabel: question.minRateDescription || String(min),
      maxLabel: question.maxRateDescription || String(max)
    }
  }, [question, isLikertScale])

  const scaleMidpoint = (scaleRange.min + scaleRange.max) / 2

  // Likert data and stats
  const likertData = useMemo((): Array<{ rawValue: unknown; value: number }> => {
    if (!isLikertScale || isSegmented) return []
    const rawData = aggregateSimple(question, responses)
    return rawData
      .map((point) => ({ rawValue: point.rawValue, value: point.value }))
      .sort((a, b) => Number(a.rawValue) - Number(b.rawValue))
  }, [question, responses, isLikertScale, isSegmented])

  const likertStats = useMemo(() => {
    if (!isLikertScale || isSegmented) return { total: 0, average: null }
    let sum = 0
    let count = 0
    likertData.forEach(d => {
      const val = Number(d.rawValue)
      if (!isNaN(val)) {
        sum += val * d.value
        count += d.value
      }
    })
    return {
      total: count,
      average: count > 0 ? (sum / count).toFixed(1) : null
    }
  }, [likertData, isLikertScale, isSegmented])

  // Standalone (non-segmented, non-per-asset) rating: five polarity bands from
  // the already-aggregated likertData. Rows stay on the 3-band helpers.
  const polarity = readPolarity(question)
  const fiveBands = useMemo(() => {
    if (!isLikertScale || isSegmented || isPerAsset) return null
    return fiveBandFromDistribution(likertData, scaleRange.min, scaleRange.max, polarity)
  }, [isLikertScale, isSegmented, isPerAsset, likertData, scaleRange.min, scaleRange.max, polarity])

  // Segmented data for Likert
  const segmentedData = useMemo(() => {
    if (!isSegmented || !isLikertScale) return []
    
    const segmentGroups: Record<string, any[]> = {}
    
    responses.forEach(r => {
      const segmentValues = new Set(getQuestionValues(r, segmentQuestion).map(String))
      segmentValues.forEach((segmentStr) => {
        if (!segmentGroups[segmentStr]) {
          segmentGroups[segmentStr] = []
        }
        segmentGroups[segmentStr].push(r)
      })
    })

    const allSegments = Object.entries(segmentGroups).map(([segmentName, segmentResponses]) => {
      const rawData = aggregateSimple(question, segmentResponses)
      
      const total = rawData.reduce((sum, d) => sum + d.value, 0)
      let sum = 0
      rawData.forEach(d => {
        const val = Number(d.rawValue)
        if (!isNaN(val)) {
          sum += val * d.value
        }
      })
      const average = total > 0 ? (sum / total).toFixed(1) : null

      return { segmentName, total, average }
    })
    
    return allSegments.sort((a, b) => b.total - a.total)
  }, [question, segmentQuestion, responses, isSegmented, isLikertScale])

  const displayedSegments = segmentedData.slice(0, CHART_LIMITS.numericMaxSegments)

  // Segmented data for continuous numeric (non-Likert)
  const segmentedContinuousData = useMemo(() => {
    if (!isSegmented || isLikertScale) return []
    return aggregateContinuousSegmented(question, segmentQuestion, responses)
  }, [question, segmentQuestion, responses, isSegmented, isLikertScale])

  // Per-asset aggregation: one average bar per asset (e.g. LP1 vs LP2 in landing-page tests)
  const perAssetData = useMemo(() => {
    if (!isLikertScale || !isPerAsset || isSegmented) return []

    const assetTotals: Record<string, { sum: number; count: number }> = {}

    responses.forEach((r: any) => {
      const value = getQuestionValue(r, question)
      if (!value || typeof value !== 'object' || Array.isArray(value)) return

      Object.entries(value as Record<string, unknown>).forEach(([assetKey, rating]) => {
        const num = Number(rating)
        if (!isNaN(num) && Number.isFinite(num)) {
          if (!assetTotals[assetKey]) assetTotals[assetKey] = { sum: 0, count: 0 }
          assetTotals[assetKey].sum += num
          assetTotals[assetKey].count++
        }
      })
    })

    return Object.entries(assetTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([assetKey, { sum, count }]) => ({
        assetKey,
        assetLabel: formatAssetReportLabel(assetKey, assetContextItems),
        average: count > 0 ? (sum / count).toFixed(1) : null,
        total: count,
      }))
  }, [question, responses, isLikertScale, isPerAsset, isSegmented, assetContextItems])

  // Empty state
  const isEmpty = isLikertScale 
    ? isPerAsset
      ? perAssetData.length === 0
      : (!isSegmented && likertData.length === 0) || (isSegmented && segmentedData.length === 0)
    : isSegmented 
      ? segmentedContinuousData.length === 0
      : histogramData.length === 0

  if (isEmpty) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
      >
        <svg className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm">No data available</span>
      </div>
    )
  }

  // =====================================================
  // PER-ASSET LIKERT VIEW (e.g. LP test: one bar per landing page)
  // =====================================================
  if (isLikertScale && isPerAsset && perAssetData.length > 0) {
    const midpointPercent = scaleToPercent(scaleMidpoint, scaleRange.min, scaleRange.max, 50)
    const totalResponses = perAssetData[0]?.total ?? 0

    return (
      <div ref={rootRef} className="w-full space-y-4">
        <div className="space-y-4">
          {perAssetData.map((asset) => {
            const avgNum = asset.average ? parseFloat(asset.average) : null
            const barPercent = avgNum !== null
              ? scaleToPercent(avgNum, scaleRange.min, scaleRange.max, 0)
              : 0

            return (
              <div key={asset.assetKey} className="space-y-2" data-testid="numeric-likert-bar-row">
                <div className="flex items-center justify-between gap-3" title={asset.assetLabel}>
                  <span className="text-sm font-medium min-w-0">{asset.assetLabel}</span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 relative h-8">
                    <div className="absolute inset-0 bg-muted/30 rounded-lg" />
                    <div
                      className="absolute top-0 bottom-0 w-px border-l-2 border-dashed border-muted-foreground/40 z-10"
                      style={{ left: `${midpointPercent}%` }}
                    />
                    {avgNum !== null && (
                      <div
                        className="absolute left-0 top-1 bottom-1 rounded-md transition-all duration-500 ease-out min-w-1"
                        style={{
                          width: `${barPercent}%`,
                          backgroundColor: effectiveBarColor,
                        }}
                        title={`Average: ${asset.average} (${asset.total} responses)`}
                      />
                    )}
                  </div>

                  <div className="w-16 shrink-0 text-right">
                    <span className="text-lg font-semibold tabular-nums">{asset.average || '-'}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center pr-16">
          <div className="flex-1 relative min-w-0">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className={scaleAxis.endpointClassName} title={scaleRange.minLabel}>{scaleRange.minLabel}</span>
              <span className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 ${scaleAxis.inlineMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </span>
              <span className={scaleAxis.endpointClassName} title={scaleRange.maxLabel}>{scaleRange.maxLabel}</span>
            </div>
            {scaleAxis.stackMidpoint && (
              <div className={`text-xs ${scaleAxis.stackedMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-sm text-muted-foreground">
          Based on {totalResponses} responses
        </div>
      </div>
    )
  }

  // =====================================================
  // CONTINUOUS NUMERIC DATA - Segmented histogram
  // =====================================================
  if (!isLikertScale && isSegmented && segmentedContinuousData.length > 0) {
    const globalMaxCount = Math.max(
      ...segmentedContinuousData.flatMap(s => s.histogram.map(h => h.count)),
      1
    )
    const axisBins = (segmentedContinuousData[0]?.histogram ?? []).map((bin) => ({
      count: bin.count,
      low: bin.start,
      high: bin.end,
      label: bin.bin,
    }))

    return (
      <div className="w-full space-y-6" data-testid="numeric-segmented-continuous-card">
        {roleChip && (
          <div>
            <span
              className="inline-block rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground"
              data-testid="numeric-price-role-chip"
            >
              {roleChip}
            </span>
          </div>
        )}
        {segmentedContinuousData.map((segment) => {
          const bins = segment.histogram.map((bin) => ({
            count: bin.count,
            low: bin.start,
            high: bin.end,
            label: bin.bin,
          }))
          return (
            <div key={segment.segmentName} className="space-y-2" data-testid={`numeric-segment-row-${segment.segmentName}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium truncate" title={segment.segmentName}>
                  {segmentLabel(segment.segmentName)}
                </span>
                <div className="flex items-center gap-3 text-sm text-muted-foreground shrink-0">
                  <span className="tabular-nums">μ={formatAmount(segment.mean)}</span>
                  <span className="tabular-nums">(n={segment.count})</span>
                </div>
              </div>
              <ContinuousBars
                bins={bins}
                maxCount={globalMaxCount}
                total={segment.count}
                barColor={effectiveBarColor}
                heightClass="h-16"
                formatTick={formatAmount}
                testIdFor={(index) => `numeric-segment-bin-${segment.segmentName}-${index}`}
                hasOverflow={segment.hasOverflow}
                showAxis={false}
              />
            </div>
          )
        })}
        <ContinuousBars
          bins={axisBins}
          maxCount={1}
          total={1}
          barColor={effectiveBarColor}
          heightClass="h-16"
          showBars={false}
          formatTick={formatAmount}
          testIdFor={() => 'numeric-segmented-axis-unused'}
          hasOverflow={segmentedContinuousData[0]?.hasOverflow ?? false}
          showAxis
          axisStartId="numeric-segmented-axis-start"
          axisEndId="numeric-segmented-axis-end"
        />
        {segmentedContinuousData[0]?.totalSegmentCount > CHART_LIMITS.numericMaxSegments && (
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground italic">
              {fillLabel(labels.showingTop, {
                shown: CHART_LIMITS.numericMaxSegments,
                total: segmentedContinuousData[0].totalSegmentCount,
              })}
            </p>
          </div>
        )}
      </div>
    )
  }

  // =====================================================
  // CONTINUOUS NUMERIC DATA - Median-led summary + histogram
  // =====================================================
  if (!isLikertScale && stats && continuousBinning) {
    const isThinBase = isLowReaderBase(stats.count)
    const labelsForBins = continuousBinLabels(continuousBinning, formatAmount)
    const counts = binCounts(rawValues, continuousBinning.edges)
    const bins = continuousBinning.edges.map((edge, index) => ({
      count: counts[index] ?? 0,
      low: edge.low,
      high: edge.high,
      label: labelsForBins[index] ?? '',
    }))
    const maxCount = Math.max(...counts, 1)

    return (
      <div className="w-full" data-testid="numeric-continuous-card">
        <div className="p-6 bg-muted/20 rounded-xl border border-border/40">
          {roleChip && (
            <div className="mb-4">
              <span
                className="inline-block rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground"
                data-testid="numeric-price-role-chip"
              >
                {roleChip}
              </span>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-x-8 gap-y-4 mb-6">
            <div data-testid="numeric-stat-median">
              <div className="text-sm text-muted-foreground">{labels.median}</div>
              <div className="text-3xl font-bold tabular-nums leading-none">{formatAmount(stats.median)}</div>
            </div>
            <div data-testid="numeric-stat-average">
              <div className="text-sm text-muted-foreground">{labels.average}</div>
              <div className="text-xl font-semibold tabular-nums leading-none">{formatAmount(stats.mean)}</div>
            </div>
            <div data-testid="numeric-stat-spread">
              <div className="text-sm text-muted-foreground">{labels.spread}</div>
              <div className="text-xl font-semibold tabular-nums leading-none">
                {formatAmount(stats.p25)} {labels.rangeSeparator} {formatAmount(stats.p75)}
              </div>
            </div>
          </div>

          <ContinuousBars
            bins={bins}
            maxCount={maxCount}
            total={stats.count}
            barColor={effectiveBarColor}
            heightClass="h-36"
            formatTick={formatAmount}
            testIdFor={(index) => `numeric-bin-${index}`}
            hasOverflow={continuousBinning.hasOverflow}
            showAxis
            axisStartId="numeric-axis-start"
            axisEndId="numeric-axis-end"
          />

          <div className="text-center text-xs text-muted-foreground mt-4" data-testid="numeric-base-line">
            {fillLabel(labels.basedOn, { count: stats.count })}
            {isThinBase ? `. ${labels.lowBase}` : ''}
            <div data-testid="numeric-stat-range">
              {labels.rangePrefix} {formatAmount(stats.min)} {labels.rangeSeparator} {formatAmount(stats.max)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isLikertScale) {
    return (
      <div className="w-full text-center text-sm text-muted-foreground" data-testid="numeric-continuous-empty">
        {labels.noData}
      </div>
    )
  }

  // =====================================================
  // LIKERT/RATING SCALE - Segmented View
  // =====================================================
  if (isSegmented && isLikertScale) {
    const midpointPercent = scaleToPercent(scaleMidpoint, scaleRange.min, scaleRange.max, 50)

    return (
      <div ref={rootRef} className="w-full space-y-4">
        <div className="space-y-4">
          {displayedSegments.map((segment) => {
            const avgNum = segment.average ? parseFloat(segment.average) : null
            const barPercent = avgNum !== null
              ? scaleToPercent(avgNum, scaleRange.min, scaleRange.max, 0)
              : 0

            return (
              <div key={segment.segmentName} className="space-y-2" data-testid="numeric-likert-bar-row">
                <div className="flex items-center justify-between gap-3" title={segmentLabel(segment.segmentName)}>
                  <span className="text-sm font-medium min-w-0">{segmentLabel(segment.segmentName)}</span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 relative h-8">
                    <div className="absolute inset-0 bg-muted/30 rounded-lg" />
                    <div
                      className="absolute top-0 bottom-0 w-px border-l-2 border-dashed border-muted-foreground/40 z-10"
                      style={{ left: `${midpointPercent}%` }}
                    />
                    {avgNum !== null && (
                      <div
                        className="absolute left-0 top-1 bottom-1 rounded-md transition-all duration-500 ease-out min-w-1"
                        style={{
                          width: `${barPercent}%`,
                          backgroundColor: effectiveBarColor,
                        }}
                        title={`Average: ${segment.average} (${segment.total} responses)`}
                      />
                    )}
                  </div>

                  <div className="w-16 shrink-0 text-right">
                    <span className="text-lg font-semibold tabular-nums">{segment.average || '-'}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center pr-16">
          <div className="flex-1 relative min-w-0">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className={scaleAxis.endpointClassName} title={scaleRange.minLabel}>{scaleRange.minLabel}</span>
              <span className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 ${scaleAxis.inlineMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </span>
              <span className={scaleAxis.endpointClassName} title={scaleRange.maxLabel}>{scaleRange.maxLabel}</span>
            </div>
            {scaleAxis.stackMidpoint && (
              <div className={`text-xs ${scaleAxis.stackedMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </div>
            )}
          </div>
        </div>

        {segmentedData.length > CHART_LIMITS.numericMaxSegments && (
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground italic">
              Showing top {CHART_LIMITS.numericMaxSegments} of {segmentedData.length} segments by respondent count.
            </p>
          </div>
        )}
      </div>
    )
  }

  // =====================================================
  // LIKERT/RATING SCALE - Non-Segmented View (five-band distribution)
  // =====================================================
  const fiveBandSegments = (fiveBands ?? []).map((band) => {
    const caption = formatFiveBandLabel(band.count, band.total)
    return {
      leftPct: band.leftPct,
      widthPct: band.widthPct,
      color: band.color,
      value: caption,
      label: band.label,
      caption: caption ? (
        <span className={bandCaptionTextClass(band.color)}>
          {caption}
        </span>
      ) : undefined,
      'data-testid': `numeric-five-band-${band.index}`,
    }
  })

  return (
    <div ref={rootRef} className="w-full space-y-6">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-32 shrink-0 text-sm font-medium text-muted-foreground">
            Overall
          </div>

          <div className="flex-1 relative h-10 min-w-0">
            {fiveBandSegments.length > 0 && (
              <DivergingBar
                className="h-10"
                segments={fiveBandSegments}
                ariaLabel={`Distribution of ${likertStats.total} responses, average ${likertStats.average ?? 'n/a'}`}
                zeroLineClassName="hidden"
                data-testid="numeric-five-band"
              />
            )}
          </div>

          <div className="w-20 shrink-0 text-right">
            <span className="text-2xl font-bold tabular-nums" data-testid="numeric-rating-average">
              {likertStats.average || '-'}
            </span>
            <span className="text-xs text-muted-foreground block">avg</span>
          </div>
        </div>
      </div>

      <div className="flex items-center pl-36 pr-20">
        <div className="flex-1 relative min-w-0">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className={scaleAxis.endpointClassName} title={scaleRange.minLabel}>{scaleRange.minLabel}</span>
            <span className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 text-xs ${scaleAxis.inlineMidpointClassName}`}>
              Midpoint ({scaleMidpoint.toFixed(1)})
            </span>
            <span className={scaleAxis.endpointClassName} title={scaleRange.maxLabel}>{scaleRange.maxLabel}</span>
          </div>
          {scaleAxis.stackMidpoint && (
            <div className={`text-xs ${scaleAxis.stackedMidpointClassName}`}>
              Midpoint ({scaleMidpoint.toFixed(1)})
            </div>
          )}
        </div>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        Based on {likertStats.total} responses
      </div>
    </div>
  )
}
